import { CoralogixRum, CoralogixEventType, type CoralogixDomain } from '@coralogix/browser';
import { env } from '@/env';

let initialized = false;

/**
 * Boot the Coralogix RUM agent.
 *
 * Idempotent — safe to call from `main.tsx` and tests.
 */
export const setupCoralogixRum = (): void => {
  if (initialized) return;
  initialized = true;

  // No public key or domain configured — skip instantiating the agent
  // entirely. Re-enable per env by setting the VITE_CORALOGIX_RUM_* vars.
  if (!env.coralogixRum.publicKey || !env.coralogixRum.domain) return;

  CoralogixRum.init({
    public_key: env.coralogixRum.publicKey,
    application: env.otel.serviceName,
    version: env.otel.serviceVersion,
    coralogixDomain: env.coralogixRum.domain as CoralogixDomain,
    environment: env.appEnv,
    sessionSampleRate: 100,
    instrumentations: {
      web_vitals: true,
      errors: true,
      interactions: true,
      fetch: true,
      xhr: true,
    },
    // Matches the broad propagation the fetch/xhr OTel instrumentations
    // already use (see otel.ts) — every request gets a traceparent header,
    // same-origin or cross-origin.
    // traceParentInHeader: {
    //   enabled: true,
    //   options: {
    //     allowedTracingUrls: [/.*/],
    //     propagateTraceHeaderCorsUrls: [/.*/],
    //   },
    // },
    beforeSend: (event) => {
      if (event?.event_context?.type === CoralogixEventType.ERROR) {
        event.screenshotId = CoralogixRum.screenshot('Auto-capture on error');
      }
      return event;
    },
    sessionRecordingConfig: {
      enable: true,
      autoStartSessionRecording: true,
      recordConsoleEvents: true,
      sessionRecordingSampleRate: 100,
      sampling: {
        mousemove: true,
        mouseInteraction: true,
        // Does not emit more than once per 150ms.
        scroll: 150,
        media: 800,
        // Record only the final value of a multi-character input, not every keystroke.
        input: 'last',
      },
      // CSD-33, Frontend Swarm — Data Privacy Critic, CRITICAL finding. 100%
      // session recording with no input masking meant every free-text field
      // this app's CLAUDE.md Data Privacy Policy documents as "never logged"
      // (health/lifecycle override reasons, QBR narratives, success-plan
      // notes, ...) was being captured VERBATIM by session replay and shipped
      // to Coralogix anyway, even though nothing ever logs it.
      //
      // `maskInputOptions` is this SDK's (`@coralogix/browser@3.23.2`,
      // confirmed against the installed package) ONLY lever for masking an
      // input's recorded VALUE — read from its bundled rrweb fork
      // (`sessionRecorder.esm.js`'s `maskInputValue`): masking is gated
      // PURELY by tag name / `<input type>`, with no per-element selector or
      // class override for VALUES (that selective mechanism — `maskTextClass`
      // / `maskTextSelector` — only masks rendered TEXT CONTENT, e.g. a
      // paragraph, never a live input's value; confirmed by reading the
      // snapshot source directly rather than assumed). So "mask this one
      // field" is not expressible here — the finest grain this version
      // offers is "mask every `<textarea>`" (`textarea: true` below), which
      // is also every OTHER multiline field in the app (QBR narratives,
      // success-plan notes, generic descriptions). That is a strictly wider
      // blast radius than this story's own Reason field, but it is the only
      // real lever that exists, and it is privacy-positive everywhere it
      // lands, not a regression. No plain `text`/`email`/etc. input types are
      // masked here — this is deliberately scoped to multiline fields only,
      // since that is what this story's own field renders as
      // (`TextField multiline`) and the only class this SDK version lets us
      // target without touching every other field's own component file (out
      // of this story's scope — see `ClientHealthAssessmentDialog.tsx`'s own
      // comment). Follow-up recommended, not implemented here: CLAUDE.md's
      // Data Privacy Policy should document a general "free-text PII fields
      // must be masked from RUM session recording" rule so a future field
      // doesn't silently reintroduce this gap in a non-`<textarea>` input.
      maskInputOptions: { textarea: true },
    },
  });
};
